import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createErrorBanner } from 'components/Banner';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { BackNavLink, Title } from 'features/App';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectActiveVenueTimezone, selectRouteParams } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { FLYOUT_ID } from './SkuPayoutReports.constants';
import { skuPayoutReportsMessages as messages } from './SkuPayoutReports.messages';
import { isOrderLineItem } from './SkuPayoutReports.types';
import SkuPayoutDetailSummary from './SkuPayoutDetailSummary';
import SkuPayoutOrderFlyout from './SkuPayoutOrderFlyout';
import SkuPayoutOrdersTable from './SkuPayoutOrdersTable';
import SkuPayoutOrdersTableMobile from './SkuPayoutOrdersTableMobile';
import useSkuPayoutExport from './useSkuPayoutExport';
import useSkuPayoutReportDetail from './useSkuPayoutReportDetail';
import './SkuPayoutReports.scss';

const SkuPayoutReportDetailInner = () => {
  const dispatch = useDispatch();
  const activeVenue = useSelector(selectActiveVenue);
  const { venueId } = activeVenue;
  const venueTimezone = useSelector(selectActiveVenueTimezone);
  const isMobile = useSelector(selectIsMobile);
  const { payoutId: payoutIdParam } = useSelector(selectRouteParams);
  const payoutId = payoutIdParam == null ? null : Number(payoutIdParam);
  const { openFlyout } = useContext(FlyoutContext);
  const [selectedLineItem, setSelectedLineItem] = useState(null);

  const { summary, transactions, totalItems, page, setPage, isLoading, error } = useSkuPayoutReportDetail({
    venueId,
    payoutId,
  });

  useEffect(() => {
    if (!error) return;
    reportError('Error fetching SKU payout detail', error);
    dispatch(createErrorBanner(messages.detailLoadError));
  }, [dispatch, error]);

  const { exportCsv, isExporting } = useSkuPayoutExport({ venueId, payoutId });

  useEffect(() => {
    if (venueId == null || payoutId == null) return;
    Segment.event('Payout Report Detail Viewed', { venueId, payoutId });
  }, [venueId, payoutId]);

  const handleRowClick = lineItem => {
    setSelectedLineItem(lineItem);
    openFlyout(FLYOUT_ID);
    Segment.event('Payout Order Flyout Viewed', {
      venueId,
      orderId: isOrderLineItem(lineItem) ? lineItem.orderId : lineItem.reservationId,
    });
  };

  const showTable = !summary || summary.isDetailAvailable;
  const OrdersTable = isMobile ? SkuPayoutOrdersTableMobile : SkuPayoutOrdersTable;
  const failedBeforeLoad = error && !summary;

  return (
    <div className="sku-payout-report-detail">
      <Title>
        <BackNavLink to={makePath.skuPayoutReports()} />
        <Message message={messages.detailPageTitle} values={{ payoutId }} />
      </Title>

      {failedBeforeLoad ? (
        <p className="sku-payout-report-detail__error">
          <Message message={messages.detailLoadError} />
        </p>
      ) : (
        <>
          <SkuPayoutDetailSummary
            summary={summary}
            venueName={activeVenue.venueName}
            city={activeVenue.addressComponents?.city}
            onDownloadCsv={exportCsv}
            isExporting={isExporting}
          />

          {showTable ? (
            <OrdersTable
              rows={transactions}
              venueTimezone={venueTimezone}
              isLoading={isLoading}
              page={page}
              totalItems={totalItems}
              onChangePage={setPage}
              onRowClick={handleRowClick}
            />
          ) : (
            <p className="sku-payout-report-detail__unavailable">
              <Message message={messages.detailUnavailable} />
            </p>
          )}
        </>
      )}

      <SkuPayoutOrderFlyout id={FLYOUT_ID} lineItem={selectedLineItem} />
    </div>
  );
};

const SkuPayoutReportDetail = () => (
  <FlyoutProvider>
    <SkuPayoutReportDetailInner />
  </FlyoutProvider>
);

export default SkuPayoutReportDetail;
